<template>
  <rest-page :fetch-endpoint="fetchEndpoint" layout="tabbed">
    <el-alert
      v-if="showOptimoveWithUsernameWarning"
      :title="$t('Integrations_Optimove_Username_Deletion_Warning')"
      type="warning"
      :closable="false"
      center
      show-icon
    >
    </el-alert>

    <page-header-new
      v-if="model"
      :breadcrumbs="breadcrumbs"
      :title="model.name"
      :description="description"
      :icon="integration.image"
      :tag="statusTag"
    >
      <template slot="actions">
        <a
          v-if="integration.urls.guide"
          :href="integration.urls.guide"
          target="_blank"
        >
          <el-button type="primary" plain>
            {{ $t('Integrations_Button_Guide') }}
          </el-button>
        </a>

        <integration-actions-dropdown
          v-if="!readonly"
          :integration="integration"
          :model="model"
        />

        <el-button
          v-if="$route.name === 'IntegrationSettings' && !readonly"
          type="primary"
          @click="saveSettings"
        >
          {{ $t('Integrations_Button_Save') }}
        </el-button>

        <el-button
          v-if="$route.name === 'IntegrationPromotions' && !isSyncEnabled"
          type="primary"
          @click="addRow"
        >
          {{ $t('Integrations_Button_Add_Promotion') }}
        </el-button>
      </template>
    </page-header-new>

    <page-tabs :tabs="tabs" v-if="model">
      <template slot="router">
        <router-view
          ref="router-component"
          :model="model"
          :model-id="model.id"
          :integration="integration"
        ></router-view>
      </template>
    </page-tabs>
  </rest-page>
</template>

<script>
import RestPage from '@/common/components/rest/RestPage'
import PageTabs from '@/common/components/page/PageTabs'
import PageHeaderNew from '@/common/components/page/PageHeaderNew'
import Integrations from '@/modules/integrations/types'
import IntegrationActionsDropdown from '@/modules/integrations/components/IntegrationActionsDropdown'
import { projectRoute, projectEndpoint } from '@/core/helpers'
import { EventBus } from '@/core/EventBus'
import { integrationsTypes } from '@/modules/integrations/enum/IntegrationsEnum'
import { OptimoveAuthenticationTypes } from '@/modules/integrations/enum/AuthenticationTypesEnum'
import { $hasFeature } from '@/core/utils/VueAcl'
import iframeTitleMixin from '@/common/mixins/iframeTitleMixin'

export default {
  name: 'IntegrationPage',

  mixins: [iframeTitleMixin],

  components: {
    RestPage,
    PageTabs,
    PageHeaderNew,
    IntegrationActionsDropdown
  },

  data() {
    return {
      model: null
    }
  },
  computed: {
    embeddedPageTitle() {
      return this.model ? this.model.name : null
    },

    breadcrumbs() {
      return [
        {
          label: this.$t('Integrations_Breadcrumb_Settings')
        },
        {
          label: this.$t('Integrations_Breadcrumb_Integrations'),
          route: projectRoute({
            name: 'IntegrationsList'
          })
        },
        {
          label: this.$t('Integrations_Breadcrumb_IntegrationDetails')
        }
      ]
    },

    description() {
      return (
        this.$t(this.integration.category) +
        ' ' +
        this.$t('Integrations_Text_Integration')
      )
    },

    tabs() {
      let tabs = [
        {
          title: this.$t('Integrations_Tab_Overview'),
          route: projectRoute({
            name: 'IntegrationOverview',
            query: { id: this.$route.query.id }
          })
        },
        {
          title: this.$t('Integrations_Tab_Settings'),
          route: projectRoute({
            name: 'IntegrationSettings',
            query: { id: this.$route.query.id }
          })
        }
      ]

      if (this.integration.components.promotions) {
        tabs.push({
          title: this.$t('Integrations_Tab_Promotions'),
          route: projectRoute({
            name: 'IntegrationPromotions',
            query: { id: this.$route.query.id }
          })
        })
      }
      if (this.integration.components.couponsets) {
        tabs.push({
          title: this.$t('Integrations_Tab_Couponsets'),
          route: projectRoute({
            name: 'IntegrationCouponsets',
            query: { id: this.$route.query.id }
          })
        })
      }

      if (this.integration.components.tasks) {
        tabs.push({
          title: this.$t('Integrations_Tab_Tasks'),
          route: projectRoute({
            name: 'IntegrationTasks',
            query: { id: this.$route.query.id }
          })
        })
      }

      if (this.integration.components.history) {
        tabs.push({
          title: this.$t('Integrations_Tab_History'),
          route: projectRoute({
            name: 'IntegrationHistory',
            query: { id: this.$route.query.id }
          })
        })
      }

      if (
        this.integration.type === 'sportradar' &&
        $hasFeature('iosLiveActivities') &&
        this.model.params.liveActivities &&
        this.integration.components.testTool
      ) {
        tabs.push({
          title: this.$t('Integrations_Tab_LiveActivitiesTestTool'),
          route: projectRoute({
            name: 'IntegrationTestTool',
            query: { id: this.$route.query.id }
          })
        })
      }

      return tabs
    },

    fetchEndpoint() {
      return projectEndpoint({
        method: 'get',
        url: `integrations/${this.$route.query.id}`,
        handler: data => {
          this.model = data
        }
      })
    },

    statusTag() {
      if (this.integration.requiresApproval && !this.model.params.approved) {
        return {
          type: 'warning',
          label: this.$t('Integrations_Gig_Text_Awaiting_Approval')
        }
      }
      if (this.model.active === true || this.model.active === 1) {
        return {
          type: 'success',
          label: this.$t('Integrations_Field_StatusActive')
        }
      } else {
        return {
          type: 'info',
          label: this.$t('Integrations_Field_StatusInactive')
        }
      }
    },

    integration() {
      let model = this.model.type ? Integrations[this.model.type] : {}

      if (model === undefined) {
        model = Integrations['dynamicpromotions']
      }
      return model
    },

    readonly() {
      return !this.$can('manageProjectTechnical')
    },

    isSyncEnabled() {
      return this.model.params.sync_enabled !== false
    },

    showOptimoveWithUsernameWarning() {
      return (
        this.model &&
        this.model.type === integrationsTypes.optimove &&
        (!this.model.params.hasOwnProperty('auth_type') ||
          this.model.params.auth_type ===
            OptimoveAuthenticationTypes.username) &&
        this.$store.state.app.integrations.optimoveWithUsername
      )
    }
  },

  methods: {
    saveSettings() {
      this.$refs['router-component'].save().then(model => {
        this.model = model
        this.$router.go()
      })
    },

    addRow() {
      EventBus.$emit('add-row-requested')
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/modules/integrations/pages/IntegrationPage.vue